import {
  casesSold,
  inventoryCases,
  type Product,
  type SaleStatDays,
  saleStat,
} from '../../../data/product';
import { compare } from '../../../helpers/column-sort';
import { formatCasesQuantity, formatUnitsQuantity } from '../../../helpers/format-quantity';
import { defineColumn, type StrictColumnDef } from '../../tanstack-table/column';
import { SalesDataPopover } from '../sales-data-popover';

const suggestedCases = (product: Product, salesStat: SaleStatDays) => {
  if (product.case_qty <= 0) {
    return 0;
  }

  const salesCases = casesSold(product, salesStat);
  const qohCases = inventoryCases(product);

  return Math.round(salesCases - qohCases);
};

export function getUnitsSoldColumn(salesStat: SaleStatDays): StrictColumnDef<Product> {
  const label = `${salesStat}d Units Sold`;

  return defineColumn<Product>({
    id: label,
    minWidth: 50,
    accessorFn: (product) => formatUnitsQuantity(saleStat(product, salesStat)),
    toCSV: (product) => ({
      [label]: saleStat(product, salesStat).toFixed(0),
    }),
    sortingFn: (rowA, rowB) => compare(saleStat(rowA, salesStat), saleStat(rowB, salesStat)),
    cell: ({ item: product, value }) => (
      <SalesDataPopover product={product} isCasesView={false} salesStat={salesStat}>
        <span className="has-tooltip">{value}</span>
      </SalesDataPopover>
    ),
    gloss: `Units sold over the last ${salesStat} days.`,
  });
}

export function getCasesSoldColumn(
  salesStat: SaleStatDays,
  onCasesClick?: (product: Product, casesToOrder: number) => void
): StrictColumnDef<Product> {
  const label = `${salesStat}d Cases Sold`;

  return defineColumn<Product>({
    id: label,
    minWidth: 60,
    accessorFn: (product) => formatCasesQuantity(saleStat(product, salesStat), product.case_qty),
    toCSV: (product) => ({
      [label]: product.case_qty ? casesSold(product, salesStat).toFixed(2) : '',
    }),
    sortingFn: (rowA, rowB) => {
      // Handle products with case_qty of 0 - place them last
      if (rowA.case_qty === 0 || rowB.case_qty === 0) {
        return compare(rowA.case_qty, rowB.case_qty);
      }

      return compare(casesSold(rowA, salesStat), casesSold(rowB, salesStat));
    },
    cell: ({ item: product, value }) => {
      const casesToOrder = suggestedCases(product, salesStat);

      const handleClick =
        onCasesClick && casesToOrder > 0
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              onCasesClick(product, casesToOrder);
            }
          : undefined;

      const className = handleClick
        ? 'bg-blue-100 hover:bg-blue-200 px-1 py-0.5 ring-1 ring-blue-200 ring-inset rounded-sm shadow-xs transition-colors cursor-pointer'
        : 'has-tooltip';

      return (
        <SalesDataPopover product={product} isCasesView={true} salesStat={salesStat}>
          <button type="button" className={className} onClick={handleClick}>
            {value}
          </button>
        </SalesDataPopover>
      );
    },
    gloss: `Cases sold over the last ${salesStat} days, based on case quantity.`,
  });
}
